// src/v2/hooks/useSeparatedEmployeeReport.js
import { useState, useCallback } from 'react';
import reportsService from '../services/reportsService';
import { useGeneralMessage } from '../../common/APICommon';
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

export const useSeparatedEmployeeReport = () => {
  const [loading, setLoading] = useState(false);
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  /**
   * Generates a professional Separated Employee Masterlist from scratch
   */
  const downloadPDF = useCallback(async (data, dateFrom, dateTo, companyName) => {
    try {
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([612, 792]); // Letter Size
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      let currentY = 742;

      // --- HEADER ---
      page.drawText("SEPARATED EMPLOYEE REPORT", { x: 50, y: currentY, size: 14, font: fontBold });
      currentY -= 20;
      page.drawText(`Period: ${dateFrom || 'N/A'} to ${dateTo || 'N/A'}`, { x: 50, y: currentY, size: 10, font });
      currentY -= 15;
      page.drawText(`Company: ${companyName}`, { x: 50, y: currentY, size: 10, font });
      currentY -= 30;

      // --- TABLE HEADERS ---
      page.drawRectangle({ x: 50, y: currentY - 20, width: 512, height: 20, color: rgb(0.9, 0.9, 0.9) });
      const cols = { id: 60, name: 140, dept: 260, hire: 360, sep: 440, reason: 510 };

      const headerSize = 8;
      page.drawText("ID", { x: cols.id, y: currentY - 14, size: headerSize, font: fontBold });
      page.drawText("Name", { x: cols.name, y: currentY - 14, size: headerSize, font: fontBold });
      page.drawText("Department", { x: cols.dept, y: currentY - 14, size: headerSize, font: fontBold });
      page.drawText("Hire Date", { x: cols.hire, y: currentY - 14, size: headerSize, font: fontBold });
      page.drawText("Sep. Date", { x: cols.sep, y: currentY - 14, size: headerSize, font: fontBold });
      page.drawText("Reason", { x: cols.reason, y: currentY - 14, size: headerSize, font: fontBold });

      currentY -= 20;

      // --- DATA ROWS ---
      if (!data || data.length === 0) {
        currentY -= 30;
        page.drawText("NO SEPARATED EMPLOYEES FOUND FOR THIS PERIOD", { x: 190, y: currentY, size: 9, font });
      } else {
        data.forEach((row, index) => {
          currentY -= 20;
          if (index % 2 === 1) page.drawRectangle({ x: 50, y: currentY, width: 512, height: 20, color: rgb(0.98, 0.98, 0.98) });

          const rowSize = 7.5;
          page.drawText(row.employeeId?.toString() || "-", { x: cols.id, y: currentY + 6, size: rowSize, font });
          page.drawText(row.fullName || "-", { x: cols.name, y: currentY + 6, size: rowSize, font });
          page.drawText(row.departmentName || "-", { x: cols.dept, y: currentY + 6, size: rowSize, font });
          page.drawText(row.hireDate || "-", { x: cols.hire, y: currentY + 6, size: rowSize, font });
          page.drawText(row.separationDate || "-", { x: cols.sep, y: currentY + 6, size: rowSize, font });
          page.drawText(row.reason || "-", { x: cols.reason, y: currentY + 6, size: rowSize, font });

          page.drawLine({ start: { x: 50, y: currentY }, end: { x: 562, y: currentY }, thickness: 0.1, color: rgb(0.8, 0.8, 0.8) });
        });
      }

      // --- DOWNLOAD ---
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Separated_Employees_${dateFrom}_to_${dateTo}.pdf`;
      link.click();

      showGeneralMessage("Separated Employee report downloaded.", true);
    } catch (err) {
      console.error(err);
      showGeneralMessage("Failed to generate report PDF.", false);
    }
  }, [showGeneralMessage]);

  const generateAndDownload = useCallback(async (params, dateFrom, dateTo, companyName) => {
    setLoading(true);
    try {
      let data = [];
      try {
        data = await reportsService.getReportData('SeparatedEmployees', params);
      } catch (apiErr) {
        console.warn("Backend not found. Falling back to Demo Mode.");
        data = [];
      }
      await downloadPDF(data || [], dateFrom, dateTo, companyName);
    } catch (err) {
      showGeneralMessage("Failed to process request.", false);
    } finally {
      setLoading(false);
    }
  }, [downloadPDF, showGeneralMessage]);

  return { loading, generateAndDownload, showMessage, message, isSuccess };
};